import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Lock, LogOut, Menu, X } from 'lucide-react';
import { getStoredUser } from '@/lib/storage';
import { useAuth } from '@/stores/auth';
import { useSchool } from '@/lib/useSchool';
import { useMyAccess } from '@/lib/useMyAccess';

export interface NavItem {
  to: string;
  label: string;
  icon?: React.ElementType;
  group?: string;
  badge?: string | number;
}

interface RoleLayoutProps {
  base?: string;
  nav: NavItem[];
  title: string;
}

/**
 * Shared shell for applicant, parent, teacher, student, bursar and desk.
 * Desktop: fixed sidebar. Phone: the sidebar slides in from a menu button and closes after you pick a page.
 */
export function RoleLayout({ nav: allNav, title, base }: RoleLayoutProps) {
  const { hidden } = useMyAccess();
  const nav = allNav.filter((n) => !hidden.includes(n.to));
  const authUser = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const stored = getStoredUser();
  const userName: string = authUser?.name || stored?.name || stored?.first_name || title;
  const brand = useSchool();
  const schoolName: string = brand.name || stored?.school?.name || 'School';
  const initials = userName.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const signOut = () => { logout(); navigate('/login', { replace: true }); };

  const grouped = nav.reduce((acc, item) => {
    const g = item.group || 'General';
    (acc[g] ??= []).push(item);
    return acc;
  }, {} as Record<string, NavItem[]>);
  const current = nav.find((n) => location.pathname.split('/').filter(Boolean).includes(n.to.replace(/^\//, '').split('/').pop() ?? ''));

  return (
    <div className="min-h-screen md:flex bg-slate-50 font-sans">
      {open && <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setOpen(false)} aria-hidden />}

      <aside className={`drawer-safe fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] md:w-64 md:sticky md:top-0 md:h-screen bg-[#0A2540] text-slate-300 flex flex-col shrink-0 transition-transform duration-200 ${open ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
        <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10 shrink-0">
          {brand.logo
            ? <img src={brand.logo} alt="" className="w-9 h-9 rounded-lg object-contain bg-white shrink-0" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
            : <div className="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center text-white font-bold shrink-0">{schoolName.charAt(0).toUpperCase()}</div>}
          <div className="min-w-0 flex-1">
            <div className="text-white font-bold text-sm leading-tight truncate">{schoolName}</div>
            <div className="text-[11px] text-slate-400 truncate">{title}</div>
          </div>
          <button className="md:hidden tap-target text-slate-300 hover:text-white" onClick={() => setOpen(false)} aria-label="Close menu"><X className="w-6 h-6" /></button>
        </div>

        <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              {Object.keys(grouped).length > 1 && <div className="px-3 mb-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{group}</div>}
              <nav className="space-y-0.5">
                {items.map((item) => (
                  <NavLink key={item.to} to={item.to}
                    className={({ isActive }) => `flex items-center justify-between px-3 py-2.5 rounded-md transition-colors text-sm font-medium ${isActive ? 'bg-green-600 text-white' : 'hover:bg-white/10 hover:text-white'}`}>
                    <span className="flex items-center gap-3">{item.icon && <item.icon className="w-4 h-4 opacity-80" />}<span>{item.label}</span></span>
                    {item.badge != null && item.badge !== '' && (
                      <span className="bg-green-600/20 text-green-300 text-[10px] font-bold px-2 py-0.5 rounded-full border border-green-600/30">{item.badge}</span>
                    )}
                  </NavLink>
                ))}
              </nav>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-white/10 shrink-0 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-xs">{initials}</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white truncate">{userName}</div>
            <div className="text-xs text-slate-400 truncate">{title}</div>
          </div>
          <button onClick={signOut} className="text-slate-400 hover:text-white p-1" title="Sign out" aria-label="Sign out"><LogOut className="w-4 h-4" /></button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden mobile-topbar sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center gap-1 px-2">
          <button onClick={() => setOpen(true)} className="tap-target text-slate-700" aria-label="Open menu"><Menu className="w-6 h-6" /></button>
          {brand.logo && <img src={brand.logo} alt="" className="w-7 h-7 rounded-md object-cover" />}
          <span className="font-semibold text-slate-900 truncate">{current?.label ?? schoolName}</span>
        </header>
        <main className="flex-1 p-4 sm:p-6 min-w-0">
          {(() => {
            const page = location.pathname.split('/').filter(Boolean)[1];
            const off = allNav.find((n) => n.to === page && hidden.includes(n.to));
            return off ? (
              <div className="max-w-md mx-auto my-16 bg-white rounded-2xl border border-slate-200 p-6 text-center">
                <Lock className="w-8 h-8 text-slate-300 mx-auto" />
                <h1 className="font-bold text-slate-900 mt-2">{off.label} is turned off</h1>
                <p className="text-sm text-slate-500 mt-1">The school has switched this page off. Contact the school if you need it.</p>
                <Link to={`${base ?? ''}/${allNav[0]?.to ?? ''}`} className="inline-block mt-4 px-4 py-2 rounded-xl bg-[#0A2540] text-white text-sm font-semibold">Back</Link>
              </div>
            ) : <Outlet />;
          })()}
        </main>
      </div>
    </div>
  );
}
